import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { AlertTriangle, Check, CreditCard, Loader2 } from 'lucide-react'
import { supabase } from '../lib/supabase'
import { STATUTORY_FALLBACK } from '../lib/cancellationTerms'
import { docFormatDate, docFormatMoney } from '../locales/documents'
import type { DocLang } from '../lib/documents'
import { Button, Card, Input } from '../ui/kit'
import { oeffneExtern } from '../lib/extern'

/**
 * Der Abschluss des Angebots, aus Sicht des Gasts: annehmen, ablehnen, anzahlen.
 *
 * Diese Fläche steht unter dem Angebot, nicht darüber — der Gast soll gelesen haben,
 * wozu er ja sagt. Sie ist `no-print`: ein Ausdruck ist ein Beleg, kein Formular.
 *
 * ════════════════════════════════════════════════════════════════════════════════
 *  DIE BESCHRIFTUNG DES KNOPFES IST NICHT VERHANDELBAR
 * ════════════════════════════════════════════════════════════════════════════════
 *  Er heißt „Zahlungspflichtig buchen" (englisch „Book with obligation to pay") und
 *  bleibt so. Das ist keine Stilfrage und keine Marketing-Entscheidung:
 *
 *    · § 312j Abs. 3 BGB verlangt für Verträge im elektronischen Geschäftsverkehr
 *      mit Verbrauchern ausdrücklich die Beschriftung „zahlungspflichtig bestellen"
 *      oder eine „entsprechende eindeutige Formulierung". In Österreich fordert
 *      § 8 FAGG dasselbe.
 *    · Die Rechtsfolge steht im selben Absatz: hält sich der Unternehmer nicht daran,
 *      KOMMT DER VERTRAG NICHT ZUSTANDE. Nicht „ist anfechtbar" — er entsteht gar
 *      nicht. Der Vermieter hätte dann eine blockierte Woche, eine kassierte
 *      Anzahlung und keinen Anspruch darauf.
 *    · „Annehmen", „Bestätigen", „Jetzt buchen", „Weiter" reichen nach herrschender
 *      Auffassung NICHT. Ein Knopf, aus dem die Zahlungspflicht nicht hervorgeht,
 *      ist genau der Fall, den die Vorschrift verhindern soll.
 *
 *  Wer diesen Text „schöner" macht, macht die Buchung unwirksam. Wenn eine Änderung
 *  wirklich nötig ist, muss sie ein Anwalt freigeben, nicht der Geschmack.
 *
 *  Aus demselben Grund steht die Übersicht unmittelbar darüber und nicht
 *  irgendwo auf der Seite: § 312j Abs. 2 BGB verlangt die wesentlichen Merkmale und
 *  den Gesamtpreis „unmittelbar bevor der Verbraucher seine Bestellung abgibt", in
 *  hervorgehobener Weise. Deshalb Einheit, Zeitraum, Gesamtpreis, Anzahlung samt
 *  Fälligkeit und Stornobedingungen in einer Blickachse mit dem Knopf.
 * ════════════════════════════════════════════════════════════════════════════════
 *
 * Die Sprache folgt dem DOKUMENT, nicht dem Browser: der Gast hat ein deutsches oder
 * englisches Angebot bekommen, und die Entscheidung darüber muss in derselben Sprache
 * dastehen wie das, worüber entschieden wird.
 */

/** Nur das, was für die Entscheidung gebraucht wird — strukturell aus `PublicDoc`. */
export interface QuoteForAcceptance {
  number: string
  status: string
  currency: string
  valid_until: string | null
  terms: string | null
  totals: { gross: number; prepayment: number; deposit: number }
  stay: {
    unit: string; property: string | null
    arrival: string; departure: string
  } | null
  issuer: {
    name: string; email: string | null; phone: string | null
    iban: string | null; bic: string | null; holder: string | null
  }
  acceptance: {
    accepted_at: string | null
    declined_at: string | null
    prepayment_due: string | null
    prepaid: boolean
    online_payment: boolean
  } | null
}

type Payment = 'stripe' | 'none' | 'paid' | 'not_connected' | 'unavailable'

interface AcceptResult {
  status: string
  accepted_at: string | null
  pay_url: string | null
  payment: Payment
  /** Ob die Vertragsbestätigung tatsächlich hinausgegangen ist. Siehe unten. */
  confirmation_mailed?: boolean
}

export default function QuoteAcceptPanel({
  doc, token, lang, onChanged,
}: {
  doc: QuoteForAcceptance
  token: string
  lang: DocLang
  onChanged: () => void
}) {
  const de = lang === 'de'
  const [params] = useSearchParams()
  const [busy, setBusy] = useState<'accept' | 'decline' | 'pay' | 'sync' | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<AcceptResult | null>(null)
  const [declineOpen, setDeclineOpen] = useState(false)
  const [declineNote, setDeclineNote] = useState('')
  const [declined, setDeclined] = useState(false)
  const [paid, setPaid] = useState(doc.acceptance?.prepaid ?? false)

  // „ZZ Testbetrieb e.U.." — Firmennamen enden oft selbst auf einen Punkt.
  const issuerName = doc.issuer.name.replace(/\.$/, '')
  const money = (n: number) => docFormatMoney(lang, n, doc.currency)
  const date = (iso: string) => docFormatDate(lang, iso.slice(0, 10))
  const when = (ts: string) => new Intl.DateTimeFormat(de ? 'de-AT' : 'en-GB', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  }).format(new Date(ts))

  const call = useCallback(async (action: string, extra: Record<string, unknown> = {}) => {
    if (!supabase) throw new Error('no_backend')
    const { data, error: e } = await supabase.functions.invoke('quote-accept', {
      body: { action, token, ...extra },
    })
    if (e) {
      // Der Fehlercode steckt im Rumpf der Antwort, nicht in der Message —
      // ohne dieses Auspacken läse der Gast „non-2xx status code".
      const res = (e as { context?: Response }).context
      if (res && typeof res.json === 'function') {
        const parsed = await res.json().catch(() => null) as { error?: string } | null
        if (parsed?.error) throw new Error(parsed.error)
      }
      throw new Error(e.message)
    }
    return data as Record<string, unknown>
  }, [token])

  // Rückkehr von der Stripe-Seite. Die URL beweist nichts — gefragt wird der Server,
  // der seinerseits Stripe fragt. Erst dann steht „Anzahlung eingegangen" da.
  useEffect(() => {
    if (params.get('paid') !== '1' || paid) return
    setBusy('sync')
    void (async () => {
      try {
        const out = await call('sync')
        if (out.paid === true) { setPaid(true); onChanged() }
      } catch { /* Der Gast hat bezahlt oder nicht; ein Fehler hier ändert nichts daran. */ }
      setBusy(null)
    })()
  }, [params, call, paid, onChanged])

  const nights = doc.stay
    ? Math.round((Date.parse(`${doc.stay.departure}T00:00:00Z`)
      - Date.parse(`${doc.stay.arrival}T00:00:00Z`)) / 86_400_000)
    : 0

  const accepted = Boolean(doc.acceptance?.accepted_at) || doc.status === 'accepted'
    || result?.status === 'accepted'
  const isDeclined = declined || Boolean(doc.acceptance?.declined_at) || doc.status === 'declined'
  const withdrawn = doc.status === 'withdrawn'
  const expired = doc.status === 'expired'
    || (!accepted && !isDeclined && doc.valid_until !== null
      && doc.valid_until < new Date().toISOString().slice(0, 10))

  /** Wen der Gast anruft, wenn etwas nicht stimmt. Steht ohnehin oben auf dem Blatt. */
  const contact = [doc.issuer.email, doc.issuer.phone].filter(Boolean).join(' · ')
  const contactLine = (
    <p className="text-sm text-muted">
      {de ? 'Bitte wenden Sie sich an ' : 'Please contact '}
      <strong className="text-text">{doc.issuer.name}</strong>
      {contact && <> — {contact}</>}
    </p>
  )

  const errorText = (code: string) => {
    switch (code) {
      // DER WICHTIGSTE FEHLER DIESER SEITE. Der Gast hat alles richtig gemacht und
      // trotzdem verloren; das muss dastehen wie eine Nachricht von einem Menschen
      // und nicht wie eine Fehlermeldung. Kein „Constraint", kein „Konflikt".
      case 'unit_taken':
        return de
          ? `Der Zeitraum ${doc.stay ? `${date(doc.stay.arrival)} – ${date(doc.stay.departure)} ` : ''}ist inzwischen vergeben — jemand anderer war ein paar Minuten schneller. Ihr Angebot wurde deshalb nicht angenommen, und es wurde Ihnen nichts berechnet.`
          : `The dates ${doc.stay ? `${date(doc.stay.arrival)} – ${date(doc.stay.departure)} ` : ''}have been taken in the meantime — someone else was a few minutes quicker. Your quote was not accepted, and you have not been charged.`
      case 'expired':
        return de
          ? `Dieses Angebot war ${doc.valid_until ? `bis zum ${date(doc.valid_until)} ` : ''}gültig und kann online nicht mehr angenommen werden.`
          : `This quote was valid ${doc.valid_until ? `until ${date(doc.valid_until)} ` : ''}and can no longer be accepted online.`
      case 'withdrawn':
        return de
          ? 'Dieses Angebot wurde zurückgezogen und kann nicht mehr angenommen werden.'
          : 'This quote has been withdrawn and can no longer be accepted.'
      case 'already_accepted':
        return de
          ? 'Dieses Angebot wurde bereits angenommen.'
          : 'This quote has already been accepted.'
      case 'already_declined':
        return de
          ? 'Dieses Angebot wurde bereits abgelehnt.'
          : 'This quote has already been declined.'
      case 'not_found':
        return de
          ? 'Dieses Angebot ist nicht (mehr) verfügbar.'
          : 'This quote is no longer available.'
      default:
        return de
          ? `Es hat nicht geklappt (${code}). Bitte versuchen Sie es noch einmal.`
          : `That did not work (${code}). Please try again.`
    }
  }

  const accept = async () => {
    setBusy('accept'); setError(null)
    try {
      const out = await call('accept') as unknown as AcceptResult
      setResult(out)
      onChanged()
    } catch (e) {
      setError(String((e as Error).message))
      // Bei jedem dieser Fehler hat sich der Zustand geändert, seit die Seite geladen
      // wurde. Neu laden, damit der Gast danach den wahren Stand vor sich hat.
      onChanged()
    }
    setBusy(null)
  }

  const decline = async () => {
    setBusy('decline'); setError(null)
    try {
      await call('decline', { message: declineNote })
      setDeclined(true)
      onChanged()
    } catch (e) {
      setError(String((e as Error).message))
      onChanged()
    }
    setBusy(null)
  }

  const payNow = async () => {
    // In der Hülle kehren diese Aufrufe beim Schließen zurück, im Browser nie —
    // dort wird die Seite verlassen. `onChanged()` holt danach den frischen
    // Stand; ohne das zeigt die Seite „offen", obwohl der Gast gezahlt hat.
    if (result?.pay_url) { await oeffneExtern(result.pay_url); onChanged(); return }
    setBusy('pay'); setError(null)
    try {
      const out = await call('pay') as { pay_url?: string | null }
      if (out.pay_url) { await oeffneExtern(out.pay_url); onChanged(); setBusy(null); return }
      setError('payment_unavailable')
    } catch (e) {
      setError(String((e as Error).message))
    }
    setBusy(null)
  }

  /* ------------------------------------------------------------ abgelehnt */
  if (isDeclined) {
    return (
      <Panel>
        <Card className="p-5 space-y-2">
          <h2 className="font-semibold">{de ? 'Angebot abgelehnt' : 'Quote declined'}</h2>
          <p className="text-sm text-muted">
            {/* Keine Behauptung, es sei jemand angerufen worden: die Absage steht in
                der Übersicht des Vermieters, verschickt wird dabei nichts. */}
            {de
              ? 'Sie haben dieses Angebot abgelehnt. Ihr Gastgeber sieht die Absage in seiner Übersicht; es wurde nichts gebucht und nichts berechnet.'
              : 'You have declined this quote. Your host will see it in their overview; nothing was booked and nothing charged.'}
          </p>
          <p className="text-sm text-muted">
            {de
              ? 'War das ein Versehen oder hat sich etwas geändert?'
              : 'A mistake, or has something changed?'}
          </p>
          {contactLine}
        </Card>
      </Panel>
    )
  }

  /* ----------------------------------------------------------- angenommen */
  if (accepted) {
    const payment = result?.payment
      ?? (doc.acceptance?.online_payment ? 'stripe' : 'not_connected')
    const amount = doc.totals.prepayment
    const acceptedAt = result?.accepted_at ?? doc.acceptance?.accepted_at ?? null

    return (
      <Panel>
        <Card className="p-5 space-y-3">
          <div className="flex items-center gap-2">
            <Check className="text-ok shrink-0" size={22} />
            <h2 className="font-semibold">
              {de ? 'Ihre Buchung ist bestätigt' : 'Your booking is confirmed'}
            </h2>
          </div>
          {doc.stay && (
            <p className="text-sm">
              {doc.stay.unit}{doc.stay.property && ` · ${doc.stay.property}`}<br />
              {date(doc.stay.arrival)} – {date(doc.stay.departure)}
              {nights > 0 && <> · {nights} {de ? (nights === 1 ? 'Nacht' : 'Nächte') : (nights === 1 ? 'night' : 'nights')}</>}
            </p>
          )}
          {acceptedAt && (
            <p className="text-xs text-muted">
              {de ? 'Angenommen am ' : 'Accepted on '}{when(acceptedAt)} · {doc.number}
            </p>
          )}

          {/*
            Nur wenn die Bestätigung wirklich hinaus ist. Der Satz ist eine Zusage —
            eine, die man nicht ins Blaue macht: ein Gast, der vergeblich auf eine
            angekündigte Mail wartet, ruft an, und der Vermieter weiß nicht, wovon er
            spricht. Bleibt sie aus, steht hier nichts; die offene Pflicht landet
            stattdessen in der Tagesübersicht des Vermieters, wo sie hingehört.
          */}
          {result?.confirmation_mailed && (
            <p className="text-sm text-muted">
              {de
                ? 'Ihre Buchungsbestätigung mit allen Vertragsangaben ist per E-Mail unterwegs — bitte bewahren Sie sie auf.'
                : 'Your booking confirmation with all contract details is on its way by e-mail — please keep it.'}
            </p>
          )}

          {paid && (
            <p className="text-sm bg-ok-soft text-ok rounded-xl px-3 py-2">
              {de ? 'Ihre Anzahlung ist eingegangen. Vielen Dank.'
                  : 'Your deposit payment has been received. Thank you.'}
            </p>
          )}

          {!paid && amount > 0 && payment === 'stripe' && (
            <>
              <p className="text-sm text-muted">
                {de
                  ? `Es fehlt noch die Anzahlung von ${money(amount)}. Sie bezahlen direkt an ${issuerName}.`
                  : `The deposit payment of ${money(amount)} is still outstanding. You pay ${issuerName} directly.`}
              </p>
              <Button variant="primary" className="w-full" disabled={busy !== null} onClick={payNow}>
                {busy === 'pay' || busy === 'sync'
                  ? <Loader2 size={16} className="animate-spin" />
                  : <CreditCard size={16} />}
                {de ? `Anzahlung bezahlen · ${money(amount)}` : `Pay deposit · ${money(amount)}`}
              </Button>
            </>
          )}

          {/* Ohne verbundenes Stripe-Konto: die Bankverbindung, die schon oben auf dem
              Angebot steht. Sie hier zu wiederholen ist keine Redundanz — das ist der
              nächste Schritt, und der Gast soll ihn nicht suchen müssen. */}
          {!paid && amount > 0 && payment !== 'stripe' && doc.issuer.iban && (
            <div className="space-y-2">
              <p className="text-sm text-muted">
                {de
                  ? `Bitte überweisen Sie die Anzahlung von ${money(amount)}${doc.acceptance?.prepayment_due ? ` bis zum ${date(doc.acceptance.prepayment_due)}` : ''}:`
                  : `Please transfer the deposit payment of ${money(amount)}${doc.acceptance?.prepayment_due ? ` by ${date(doc.acceptance.prepayment_due)}` : ''}:`}
              </p>
              <div className="text-sm bg-surface-2 rounded-xl p-3 space-y-1">
                {doc.issuer.holder && (
                  <Row label={de ? 'Kontoinhaber' : 'Account holder'} value={doc.issuer.holder} />
                )}
                <Row label="IBAN" value={doc.issuer.iban} />
                {doc.issuer.bic && <Row label="BIC" value={doc.issuer.bic} />}
                <Row label={de ? 'Verwendungszweck' : 'Reference'} value={doc.number} />
              </div>
            </div>
          )}

          {!paid && amount > 0 && payment !== 'stripe' && !doc.issuer.iban && (
            <p className="text-sm text-muted">
              {de
                ? `Für die Anzahlung von ${money(amount)} meldet sich Ihr Gastgeber bei Ihnen.`
                : `Your host will be in touch about the deposit payment of ${money(amount)}.`}
            </p>
          )}

          {amount <= 0 && (
            <p className="text-sm text-muted">
              {de ? 'Eine Anzahlung ist für diese Buchung nicht vorgesehen.'
                  : 'No deposit payment is required for this booking.'}
            </p>
          )}

          {error && <ErrorLine>{errorText(error)}</ErrorLine>}
        </Card>
      </Panel>
    )
  }

  /* ----------------------------------------- zurückgezogen oder abgelaufen */
  if (withdrawn || expired) {
    return (
      <Panel>
        <Card className="p-5 space-y-2">
          <h2 className="font-semibold">
            {withdrawn
              ? (de ? 'Angebot zurückgezogen' : 'Quote withdrawn')
              : (de ? 'Angebot abgelaufen' : 'Quote expired')}
          </h2>
          <p className="text-sm text-muted">{errorText(withdrawn ? 'withdrawn' : 'expired')}</p>
          <p className="text-sm text-muted">
            {de
              ? 'Ihr Gastgeber kann Ihnen ein neues Angebot schicken — die Preise und die Verfügbarkeit können sich inzwischen geändert haben.'
              : 'Your host can send you a new quote — prices and availability may have changed since.'}
          </p>
          {contactLine}
        </Card>
      </Panel>
    )
  }

  /* ------------------------------------------------------- offen: der Knopf */
  const amount = doc.totals.prepayment
  const due = doc.acceptance?.prepayment_due ?? null

  return (
    <Panel>
      <Card className="p-5 space-y-4">
        <h2 className="font-semibold">
          {de ? 'Angebot annehmen' : 'Accept this quote'}
        </h2>

        {/* Die Übersicht unmittelbar vor dem Knopf — siehe der Kommentar oben. */}
        <div className="border border-border rounded-xl divide-y divide-border">
          {doc.stay && (
            <div className="p-3 space-y-0.5">
              <div className="text-xs text-muted">{de ? 'Einheit und Zeitraum' : 'Unit and dates'}</div>
              <div className="font-medium">
                {doc.stay.unit}{doc.stay.property && ` · ${doc.stay.property}`}
              </div>
              <div className="text-sm">
                {date(doc.stay.arrival)} – {date(doc.stay.departure)}
                {nights > 0 && <> · {nights} {de ? (nights === 1 ? 'Nacht' : 'Nächte') : (nights === 1 ? 'night' : 'nights')}</>}
              </div>
            </div>
          )}
          <div className="p-3 flex items-baseline justify-between gap-3">
            <span className="text-sm">{de ? 'Gesamtpreis' : 'Total price'}</span>
            <strong className="text-lg">{money(doc.totals.gross)}</strong>
          </div>
          {amount > 0 && (
            <div className="p-3 flex items-baseline justify-between gap-3">
              <span className="text-sm">
                {de ? 'Davon Anzahlung' : 'Deposit payment'}
                <span className="block text-xs text-muted">
                  {due
                    ? (de ? `fällig bis ${date(due)}` : `due by ${date(due)}`)
                    : (de ? 'im nächsten Schritt' : 'in the next step')}
                </span>
              </span>
              <strong>{money(amount)}</strong>
            </div>
          )}
          {doc.totals.deposit > 0 && (
            <div className="p-3 flex items-baseline justify-between gap-3">
              <span className="text-sm">
                {de ? 'Kaution' : 'Security deposit'}
                <span className="block text-xs text-muted">
                  {de ? 'wird gesondert eingehoben und zurückgezahlt'
                      : 'collected separately and refunded'}
                </span>
              </span>
              <strong>{money(doc.totals.deposit)}</strong>
            </div>
          )}
          <div className="p-3 space-y-1">
            <div className="text-xs text-muted">
              {de ? 'Stornobedingungen' : 'Cancellation terms'}
            </div>
            {/* NIE ERFINDEN. Stehen keine Bedingungen im Angebot, dann erfindet die
                Seite keine — ein Platzhaltertext wäre eine Bedingung, die niemand
                vereinbart hat, und würde im Streitfall gegen den Vermieter ausgelegt.

                ABER AUCH NICHT WARNEN. Hier stand bis 27.07.2026 „Bitte klären Sie vor
                der Buchung mit Ihrem Gastgeber, was bei einer Absage gilt" — in
                Warnfarbe, unmittelbar über dem Knopf, mit dem der Gast buchen will. Der
                Satz war sachlich falsch: es ist nichts ungeklärt. Ohne Vereinbarung
                gelten die gesetzlichen Bestimmungen, und die kennen beide Seiten nicht
                schlechter als eine Klausel. Was blieb, war ein Zweifel kurz vor der
                Entscheidung — und eine Rückfrage beim Vermieter, auf die er keine
                bessere Antwort hat als „das Gesetz". */}
            {doc.terms
              ? <p className="text-sm whitespace-pre-line">{doc.terms}</p>
              : (
                <p className="text-sm text-muted">
                  {de ? STATUTORY_FALLBACK.de : STATUTORY_FALLBACK.en}
                </p>
              )}
          </div>

          {/*
            ═══════════════════════════════════════════════════════════════════════
             WORTLAUT DES WIDERRUFSHINWEISES — BEWUSSTE ENTSCHEIDUNG, PRÜFUNG OFFEN
            ═══════════════════════════════════════════════════════════════════════
             Dieser Text wird auf AUSDRÜCKLICHE ENTSCHEIDUNG DES BETREIBERS vom
             27.07.2026 ausgeliefert. Eine ANWALTLICHE PRÜFUNG STEHT AUS; der
             Betreiber trägt das Risiko bewusst und lässt die Formulierung später
             prüfen. Derselbe Wortlaut steht in `src/lib/mailTemplates.ts`
             (`withdrawalTitle` / `withdrawalBody`) und geht mit der
             Vertragsbestätigung hinaus — wer ihn hier ändert, muss ihn dort ebenso
             ändern, sonst steht in der Bestätigung etwas anderes als das, worauf
             der Gast geklickt hat.

             Fundstellen: § 18 Abs. 1 Z 10 FAGG (Österreich),
                          § 312g Abs. 2 Nr. 9 BGB (Deutschland).

             WARUM ER HIER STEHT, obwohl das Widerrufsrecht ausgeschlossen IST: die
             INFORMATION über den Ausschluss schuldet der Unternehmer trotzdem, und
             zwar vor der Bestellung. Ein Gast, der glaubt, er könne wie bei einer
             Onlinebestellung binnen vierzehn Tagen zurücktreten, entscheidet auf
             falscher Grundlage. Deshalb in derselben Blickachse wie der Knopf und
             direkt unter dem, was stattdessen gilt.

             BEIDE RECHTSORDNUNGEN, weil der Betrieb in Österreich sitzen und der
             Gast in Deutschland wohnen kann: dort heißt es Widerrufsrecht, hier
             Rücktrittsrecht, und wer nur eines nennt, lässt die Hälfte der Gäste im
             Unklaren darüber, ob der Satz sie überhaupt betrifft.

             ER STEHT AUCH DANN DA, WENN DER BETRIEB NICHTS HINTERLEGT HAT. Er hängt
             am Gesetz und nicht am Text des Vermieters — der Hinweis darüber, dass
             die gesetzlichen Bestimmungen gelten, bleibt daneben stehen und
             widerspricht ihm nicht.
            ═══════════════════════════════════════════════════════════════════════
          */}
          <div className="p-3 space-y-1">
            <div className="text-xs text-muted">
              {de ? 'Kein Rücktritts- oder Widerrufsrecht' : 'No right of withdrawal'}
            </div>
            <p className="text-sm">
              {de
                ? 'Bei Verträgen über die Beherbergung zu einem bestimmten Termin besteht kein gesetzliches Rücktritts- bzw. Widerrufsrecht (§ 18 Abs. 1 Z 10 FAGG in Österreich, § 312g Abs. 2 Nr. 9 BGB in Deutschland).'
                : 'For contracts for the provision of accommodation on a specific date, there is no statutory right of withdrawal or cancellation.'}
            </p>
          </div>
        </div>

        {error && <ErrorLine>{errorText(error)}</ErrorLine>}
        {error === 'unit_taken' && contactLine}

        {/*
          NICHT UMBENENNEN. „Zahlungspflichtig buchen" ist die vom Gesetz verlangte
          Formulierung (§ 312j Abs. 3 BGB, § 8 FAGG). Ein netterer Text macht den
          Vertrag unwirksam — die ausführliche Begründung steht oben in dieser Datei.
        */}
        <Button variant="primary" className="w-full text-base py-3"
          disabled={busy !== null} onClick={accept}>
          {busy === 'accept' && <Loader2 size={16} className="animate-spin" />}
          {de ? 'Zahlungspflichtig buchen' : 'Book with obligation to pay'}
        </Button>

        <p className="text-xs text-muted text-center">
          {de
            ? `Mit dem Klick nehmen Sie das Angebot ${doc.number} verbindlich an; es entsteht ein Beherbergungsvertrag mit ${issuerName}.`
            : `By clicking you accept quote ${doc.number} as binding; an accommodation contract with ${issuerName} comes into effect.`}
        </p>

        {/* Ablehnen muss genauso möglich sein wie zusagen. Ein Angebot, auf das man
            nur ja sagen kann, lässt den Vermieter im Ungewissen — er hält die Woche
            frei für jemanden, der längst woanders gebucht hat. */}
        <div className="pt-1 border-t border-border">
          {!declineOpen ? (
            <button className="w-full text-sm text-muted underline py-2"
              onClick={() => setDeclineOpen(true)}>
              {de ? 'Angebot ablehnen' : 'Decline this quote'}
            </button>
          ) : (
            <div className="space-y-2 pt-3">
              <p className="text-sm text-muted">
                {de
                  ? 'Schade. Möchten Sie Ihrem Gastgeber kurz sagen, warum? (freiwillig)'
                  : 'A shame. Would you like to tell your host why? (optional)'}
              </p>
              <Input
                value={declineNote}
                maxLength={500}
                onChange={(e) => setDeclineNote(e.target.value)}
                placeholder={de ? 'z. B. andere Termine' : 'e.g. dates no longer work'}
              />
              <div className="flex gap-2">
                <Button variant="secondary" className="flex-1"
                  disabled={busy !== null} onClick={() => setDeclineOpen(false)}>
                  {de ? 'Zurück' : 'Back'}
                </Button>
                <Button variant="danger" className="flex-1"
                  disabled={busy !== null} onClick={decline}>
                  {busy === 'decline' && <Loader2 size={16} className="animate-spin" />}
                  {de ? 'Ablehnen' : 'Decline'}
                </Button>
              </div>
            </div>
          )}
        </div>
      </Card>
    </Panel>
  )
}

function Panel({ children }: { children: React.ReactNode }) {
  return (
    <div className="no-print px-4 pb-8 -mt-2">
      <div className="max-w-lg mx-auto">{children}</div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between gap-3">
      <span className="text-muted">{label}</span>
      <span className="font-medium text-right break-all">{value}</span>
    </div>
  )
}

function ErrorLine({ children }: { children: React.ReactNode }) {
  return (
    <p className="text-sm bg-danger-soft text-danger rounded-xl px-3 py-2 flex gap-2">
      <AlertTriangle size={16} className="shrink-0 mt-0.5" />
      <span>{children}</span>
    </p>
  )
}
